import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, Sparkles, User, LogOut, ShieldAlert, UploadCloud } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import LanguageToggle from "./LanguageToggle";

export default function Navbar() {
  const { t, lang } = useLang();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const links = [
    { to: "/services", label: t("nav.services") || "Services" },
    { to: "/tcf", label: t("nav.tcf") || "Prépa & Diagnostic TCF" },
    { to: "/registry", label: t("nav.registry") || "Registre Talents" },
    { to: "/events", label: t("nav.events") || "Événements" },
    { to: "/about", label: t("nav.about") || "À propos" },
    { to: "/contact", label: t("nav.contact") || "Contact" },
  ];

  return (
    <>
      {/* Top micro-bar for prestige & authority */}
      <div className="bg-[#030B14] text-white/80 text-[11px] font-medium py-1.5 px-4 hidden md:block border-b border-white/5">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-[#D71920]/20 text-[#EF4444] border border-[#D71920]/30">
              🇨🇦 IRCC
            </span>
            <span className="text-white/75">
              {lang === "fr"
                ? "Immigration francophone & Catégorie prioritaire Entrée Express Canada"
                : "Francophone Immigration & Express Entry Priority Category Canada"}
            </span>
          </div>
          <div className="flex items-center gap-5 text-white/60">
            <Link to="/tcf/diagnostic" className="hover:text-white transition flex items-center gap-1.5">
              <Sparkles size={12} className="text-[#D7A33D]" />
              <span>{lang === "fr" ? "Simulateur TCF gratuit" : "Free TCF Simulator"}</span>
            </Link>
            <span className="text-white/20">|</span>
            <a href="mailto:contact@jnic.org" className="hover:text-white transition">
              contact@jnic.org
            </a>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={`sticky top-0 inset-x-0 z-40 transition-all duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-md shadow-[0_8px_30px_rgb(0,0,0,0.06)] border-b border-slate-200/80"
            : "bg-white border-b border-slate-100"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 h-[72px] flex items-center justify-between">
          {/* Logo & Brand Identity */}
          <Link to="/" className="flex items-center gap-3 group" id="nav-brand-logo">
            <div className="relative shrink-0">
              <img
                src="/logo.png"
                alt="JNIC"
                width="44"
                height="44"
                loading="eager"
                decoding="sync"
                className="w-11 h-11 rounded-full object-cover ring-2 ring-[#D7A33D]/40 group-hover:ring-[#D71920]/60 transition-all duration-300"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "/jnic-premium-logo.png";
                }}
              />
              <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-500 border-2 border-white rounded-full"></span>
            </div>
            <div className="leading-tight">
              <div className="flex items-center gap-1.5">
                <span className="font-serif text-xl font-bold tracking-tight text-[#06162A]">
                  JN Immigration
                </span>
              </div>
              <span className="block text-[9px] tracking-[0.26em] uppercase font-bold text-[#D71920]">
                Consulting
              </span>
            </div>
          </Link>

          {/* Desktop Navigation Links */}
          <nav className="hidden xl:flex items-center gap-1 lg:gap-2">
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `px-3.5 py-2 rounded-lg text-[13px] font-semibold transition-all duration-200 ${
                    isActive
                      ? "text-[#D71920] bg-red-50/70"
                      : "text-[#06162A]/80 hover:text-[#06162A] hover:bg-slate-50"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          {/* Desktop Right Actions */}
          <div className="hidden xl:flex items-center gap-3.5">
            <LanguageToggle />

            {/* DropBox Guest Upload Link */}
            <Link
              to="/depot-documents"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-[#06162A] bg-slate-100 hover:bg-slate-200/80 border border-slate-300/60 transition shadow-xs"
              title={lang === "fr" ? "Dépôt sécurisé de documents sans compte" : "Upload documents without signing in"}
            >
              <UploadCloud size={13} className="text-[#D71920]" />
              <span>{lang === "fr" ? "Dépôt Documents" : "Drop Documents"}</span>
            </Link>

            {/* Diagnostic Quick Link */}
            <Link
              to="/tcf/diagnostic"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-[#06162A] bg-amber-50/80 border border-amber-200/70 hover:bg-amber-100 transition shadow-xs"
            >
              <Sparkles size={13} className="text-[#D7A33D]" />
              <span>Diagnostic TCF</span>
            </Link>

            {user ? (
              <div className="flex items-center gap-2 pl-2 border-l border-slate-200">
                <Link
                  to="/dashboard"
                  className="flex items-center gap-1.5 text-xs font-semibold text-[#06162A] px-2.5 py-1.5 rounded-lg hover:bg-slate-100 transition"
                >
                  <User size={14} className="text-slate-600" />
                  <span>{t("nav.dashboard") || "Tableau de bord"}</span>
                </Link>

                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    data-testid="nav-admin"
                    className="flex items-center gap-1 text-xs font-bold text-[#D71920] bg-red-50 px-2.5 py-1.5 rounded-lg border border-red-200/60 hover:bg-red-100 transition"
                  >
                    <ShieldAlert size={13} />
                    <span>Admin</span>
                  </Link>
                )}

                <button
                  onClick={logout}
                  title={t("nav.logout") || (lang === "fr" ? "Déconnexion" : "Sign out")}
                  className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition"
                >
                  <LogOut size={14} />
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="text-xs font-semibold text-slate-600 hover:text-[#06162A] px-2.5 py-1.5 rounded-lg hover:bg-slate-50 transition"
              >
                {t("nav.login") || (lang === "fr" ? "Connexion" : "Sign in")}
              </Link>
            )}

            {/* Primary Booking CTA */}
            <Link
              to="/booking"
              id="navbar-booking-cta"
              className="inline-flex items-center gap-2 rounded-full bg-[#D71920] text-white px-5 py-2.5 text-xs font-bold shadow-[0_4px_14px_rgba(215,25,32,0.3)] hover:bg-[#B91219] hover:shadow-[0_6px_20px_rgba(215,25,32,0.4)] active:scale-98 transition-all"
            >
              <span>{t("nav.book") || (lang === "fr" ? "Réserver" : "Book a call")}</span>
              <ArrowRight size={14} />
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="flex items-center gap-2 xl:hidden">
            <LanguageToggle />
            <button
              onClick={() => setOpen(true)}
              className="p-2 rounded-lg text-slate-700 hover:bg-slate-100 transition focus:outline-none"
              aria-label={lang === "fr" ? "Ouvrir le menu" : "Open menu"}
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Drawer Overlay */}
      {open && (
        <div className="fixed inset-0 z-50 bg-[#06162A] text-white xl:hidden flex flex-col justify-between animate-fade-up">
          <div>
            <div className="h-[72px] px-6 flex justify-between items-center border-b border-white/10">
              <div className="flex items-center gap-3">
                <img
                  src="/logo.png"
                  alt="JNIC"
                  width="40"
                  height="40"
                  loading="eager"
                  decoding="sync"
                  className="w-10 h-10 rounded-full object-cover ring-2 ring-[#D7A33D] shrink-0"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "/jnic-premium-logo.png";
                  }}
                />
                <div>
                  <span className="font-serif text-lg font-bold block leading-none">JNIC</span>
                  <span className="text-[9px] uppercase tracking-[0.2em] text-[#EF4444]">
                    Consulting
                  </span>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-2 text-white/80 hover:text-white rounded-lg hover:bg-white/10 transition"
              >
                <X size={24} />
              </button>
            </div>

            <nav className="px-6 py-8 flex flex-col gap-4">
              <Link
                to="/tcf/diagnostic"
                onClick={() => setOpen(false)}
                className="flex items-center justify-between p-3.5 rounded-xl bg-amber-400/10 border border-amber-400/30 text-amber-300 font-semibold text-sm mb-2"
              >
                <span className="flex items-center gap-2">
                  <Sparkles size={16} />
                  <span>{lang === "fr" ? "Simulateur & Diagnostic TCF" : "TCF Diagnostic & Simulator"}</span>
                </span>
                <ArrowRight size={16} />
              </Link>

              {links.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `text-xl font-serif py-1.5 transition ${
                      isActive ? "text-[#EF4444] font-bold pl-2 border-l-2 border-[#EF4444]" : "text-white/80 hover:text-white"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}

              <NavLink
                to="/depot-documents"
                onClick={() => setOpen(false)}
                className="text-lg font-serif py-1.5 text-[#D7A33D] flex items-center gap-2"
              >
                <UploadCloud size={18} />
                <span>{lang === "fr" ? "Dépôt Sécurisé (Sans compte)" : "Document Drop Box (Guest)"}</span>
              </NavLink>

              {user?.role === "admin" && (
                <NavLink
                  to="/admin"
                  onClick={() => setOpen(false)}
                  className="text-lg font-serif py-1.5 text-amber-400"
                >
                  Admin Console
                </NavLink>
              )}

              {user ? (
                <NavLink
                  to="/dashboard"
                  onClick={() => setOpen(false)}
                  className="text-lg font-serif py-1.5 text-white/90"
                >
                  {t("nav.dashboard") || "Mon Espace Client"}
                </NavLink>
              ) : (
                <NavLink
                  to="/login"
                  onClick={() => setOpen(false)}
                  className="text-lg font-serif py-1.5 text-white/60"
                >
                  {t("nav.login") || "Connexion"}
                </NavLink>
              )}
            </nav>
          </div>

          <div className="p-6 border-t border-white/10 bg-black/20 flex flex-col gap-4">
            <Link
              to="/booking"
              onClick={() => setOpen(false)}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-[#D71920] text-white font-bold text-sm shadow-lg hover:bg-[#B91219] transition"
            >
              <span>{t("nav.book") || "Réserver ma consultation"}</span>
              <ArrowRight size={16} />
            </Link>

            <div className="flex items-center justify-between text-xs text-white/50 pt-2">
              <span>JN Immigration Consulting</span>
              <LanguageToggle variant="dark" />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
